// Security utilities for input validation and sanitization

// HTML sanitization function to prevent XSS
export const sanitizeHTML = (input) => {
  if (typeof input !== 'string') return '';
  
  // Remove dangerous HTML tags and attributes
  const dangerous = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
  const dangerousAttributes = /\s*(on\w+|javascript:|data:|vbscript:)[^>\s]*/gi;
  const htmlTags = /<[^>]*>/g;
  
  return input
    .replace(dangerous, '')
    .replace(dangerousAttributes, '')
    .replace(htmlTags, '')
    .trim();
};

// Validate and sanitize user input
export const validateInput = {
  // Username validation (3-15 chars, alphanumeric + underscore)
  username: (username) => {
    if (!username || typeof username !== 'string') return { valid: false, error: 'Username is required' };
    
    const sanitized = sanitizeHTML(username).toLowerCase();
    const usernameRegex = /^[a-zA-Z0-9_]{3,15}$/;
    
    if (!usernameRegex.test(sanitized)) {
      return { valid: false, error: 'Username must be 3-15 characters (letters, numbers, underscore only)' };
    }
    
    // Check for banned words
    const bannedWords = ['admin', 'moderator', 'system', 'null', 'undefined', 'root', 'test'];
    if (bannedWords.some(word => sanitized.includes(word))) {
      return { valid: false, error: 'Username contains restricted words' };
    }
    
    return { valid: true, value: sanitized };
  },

  // Display name validation (1-50 chars)
  displayName: (displayName) => {
    if (!displayName || typeof displayName !== 'string') return { valid: false, error: 'Display name is required' };
    
    const sanitized = sanitizeHTML(displayName).trim();
    
    if (sanitized.length === 0) {
      return { valid: false, error: 'Display name cannot be empty' };
    }
    
    if (sanitized.length > 50) {
      return { valid: false, error: 'Display name must be 50 characters or less' };
    }
    
    // Check for excessive special characters
    const specialCharCount = (sanitized.match(/[^\w\s]/g) || []).length;
    if (specialCharCount > sanitized.length * 0.3) {
      return { valid: false, error: 'Display name contains too many special characters' };
    }
    
    return { valid: true, value: sanitized };
  },

  // Comment/text validation (1-500 chars)
  comment: (comment) => {
    if (!comment || typeof comment !== 'string') return { valid: false, error: 'Comment is required' };
    
    const sanitized = sanitizeHTML(comment).trim();
    
    if (sanitized.length === 0) {
      return { valid: false, error: 'Comment cannot be empty' };
    }
    
    if (sanitized.length > 500) {
      return { valid: false, error: 'Comment must be 500 characters or less' };
    }
    
    // Check for spam patterns
    const repeated = /(.)\1{4,}/g; // 5+ repeated characters
    if (repeated.test(sanitized)) {
      return { valid: false, error: 'Comment contains excessive repeated characters' };
    }
    
    return { valid: true, value: sanitized };
  },

  // Bio validation (0-200 chars)
  bio: (bio) => {
    if (!bio) return { valid: true, value: '' };
    if (typeof bio !== 'string') return { valid: false, error: 'Bio must be text' };
    
    const sanitized = sanitizeHTML(bio).trim();
    
    if (sanitized.length > 200) {
      return { valid: false, error: 'Bio must be 200 characters or less' };
    }
    
    return { valid: true, value: sanitized };
  },

  // Question text validation
  question: (question) => {
    if (!question || typeof question !== 'string') return { valid: false, error: 'Question is required' };
    
    const sanitized = sanitizeHTML(question).trim();
    
    if (sanitized.length === 0) {
      return { valid: false, error: 'Question cannot be empty' };
    }
    
    if (sanitized.length > 200) {
      return { valid: false, error: 'Question must be 200 characters or less' };
    }
    
    return { valid: true, value: sanitized };
  },

  // Option validation (for polls)
  option: (option) => {
    if (!option || typeof option !== 'string') return { valid: false, error: 'Option is required' };
    
    const sanitized = sanitizeHTML(option).trim();
    
    if (sanitized.length === 0) {
      return { valid: false, error: 'Option cannot be empty' };
    }
    
    if (sanitized.length > 100) {
      return { valid: false, error: 'Option must be 100 characters or less' };
    }
    
    return { valid: true, value: sanitized };
  }
};

// Rate limiting utilities
export const rateLimiter = {
  // Check if action is rate limited
  isRateLimited: (action, userId, maxAttempts = 5, windowMs = 60000) => {
    const key = `rateLimit_${action}_${userId}`;
    const now = Date.now();
    const attempts = JSON.parse(localStorage.getItem(key) || '[]');
    
    // Remove expired attempts
    const validAttempts = attempts.filter(timestamp => now - timestamp < windowMs);
    
    if (validAttempts.length >= maxAttempts) {
      return { limited: true, resetTime: validAttempts[0] + windowMs };
    }
    
    // Add current attempt
    validAttempts.push(now);
    localStorage.setItem(key, JSON.stringify(validAttempts));
    
    return { limited: false };
  }
};

// Content moderation utilities
export const contentModeration = {
  // Check for potentially harmful content
  checkContent: (content) => {
    if (!content || typeof content !== 'string') return { safe: true };
    
    const lowercaseContent = content.toLowerCase();
    
    // Basic profanity filter (extend as needed)
    const profanityWords = [
      // Add actual words as needed for your app
      'spam', 'scam', 'hack', 'exploit'
    ];
    
    const containsProfanity = profanityWords.some(word => 
      lowercaseContent.includes(word)
    );
    
    // Check for potential phishing/malicious links
    const suspiciousPatterns = [
      /bit\.ly/gi,
      /tinyurl/gi,
      /click.*here/gi,
      /free.*money/gi,
      /win.*prize/gi
    ];
    
    const containsSuspiciousContent = suspiciousPatterns.some(pattern => 
      pattern.test(content)
    );
    
    return {
      safe: !containsProfanity && !containsSuspiciousContent,
      flags: {
        profanity: containsProfanity,
        suspicious: containsSuspiciousContent
      }
    };
  }
};

// File upload security
export const fileUploadSecurity = {
  // Validate file before upload
  validateFile: (file, options = {}) => {
    const {
      maxSize = 5 * 1024 * 1024, // 5MB default
      allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    } = options;
    
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }
    
    // Check file size
    if (file.size > maxSize) {
      return { valid: false, error: `File too large. Maximum size: ${maxSize / 1024 / 1024}MB` };
    }
    
    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Invalid file type. Only images are allowed.' };
    }
    
    // Check file extension matches MIME type
    const extension = file.name.split('.').pop().toLowerCase();
    const validExtensions = {
      'image/jpeg': ['jpg', 'jpeg'],
      'image/png': ['png'],
      'image/gif': ['gif'],
      'image/webp': ['webp']
    };
    
    const expectedExtensions = validExtensions[file.type] || [];
    if (!expectedExtensions.includes(extension)) {
      return { valid: false, error: 'File extension does not match file type' };
    }
    
    return { valid: true };
  }
};

// CSRF protection utility
export const csrfProtection = {
  generateToken: () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  },
  
  setToken: (token) => {
    sessionStorage.setItem('csrfToken', token);
  },
  
  getToken: () => {
    return sessionStorage.getItem('csrfToken');
  },
  
  validateToken: (token) => {
    const storedToken = sessionStorage.getItem('csrfToken');
    return token && storedToken && token === storedToken;
  }
};

// Authentication helpers
export const authSecurity = {
  // Validate email format
  isValidEmail: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },
  
  // Check password strength
  checkPasswordStrength: (password) => {
    if (!password || password.length < 8) {
      return { strong: false, error: 'Password must be at least 8 characters' };
    }
    
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    const score = [hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length;
    
    if (score < 3) {
      return { 
        strong: false, 
        error: 'Password must contain at least 3 of: uppercase, lowercase, number, special character' 
      };
    }
    
    return { strong: true };
  }
};